import React, { useState, useCallback } from 'react';
import {
    Box,
    Container,
    Typography,
    Stack,
    TextField,
    InputAdornment,
    IconButton,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Accept } from 'react-dropzone';

import PDFToolWorkspace from '../components/PDFToolWorkspace';
import ToolActionArea, { ToolStatus, OutputFile } from '../components/ToolActionArea';
import PdfViewerModal, { ViewerFile } from '../components/PdfViewerModal';
import StyledFAQ from '../components/StyledFAQ';
import { decryptPdf, downloadFile } from '../utils/pdfUtils';

interface FileWithId {
    id: string;
    file: File;
}

const PdfDecryptor: React.FC = () => {
    const { t } = useTranslation();
    const [files, setFiles] = useState<FileWithId[]>([]);
    const [status, setStatus] = useState<ToolStatus>('idle');
    const [outputFiles, setOutputFiles] = useState<OutputFile[]>([]);
    const [error, setError] = useState<string>('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // Viewer state
    const [viewerFile, setViewerFile] = useState<ViewerFile | null>(null);
    const handleOpenFileViewer = (file: ViewerFile) => setViewerFile(file);
    const handleCloseFileViewer = () => setViewerFile(null);

    const resetProcessingState = useCallback(() => {
        setStatus('idle');
        setOutputFiles([]);
        setError('');
    }, []);

    const handleDrop = useCallback((acceptedFiles: File[]) => {
        const newFile = acceptedFiles[acceptedFiles.length - 1];
        if (newFile) {
            setFiles([{ id: uuidv4(), file: newFile }]);
        }
        resetProcessingState();
    }, [resetProcessingState]);

    const handleDelete = useCallback((fileId: string) => {
        setFiles(currentFiles => currentFiles.filter(f => f.id !== fileId));
    }, []);

    const handleReset = useCallback(() => {
        setFiles([]);
        resetProcessingState();
        setPassword('');
    }, [resetProcessingState]);

    const handleDecrypt = async () => {
        if (!files[0]) return;

        setStatus('loading');
        setError('');
        try {
            const decryptedBytes = await decryptPdf(files[0].file, password);
            const baseName = files[0].file.name.replace(/\.pdf$/i, '');
            const output: OutputFile = {
                name: `${baseName}-decrypted.pdf`,
                data: decryptedBytes,
                size: decryptedBytes.length,
            };
            setOutputFiles([output]);
            setStatus('success');
        } catch (e: any) {
            console.error(e);
            // Use the specific error message from pdfUtils or a generic one
            const errorMessage = t(e.message || 'pdf_decrypt_error_generic', { ns: 'tools' });
            setError(errorMessage);
            setStatus('error');
        }
    };

    const handleDownloadSingle = (fileName: string) => {
        const fileToDownload = outputFiles.find(f => f.name === fileName);
        if (fileToDownload) {
            downloadFile(fileToDownload.data, fileToDownload.name);
        }
    };

    const handlePreviewOutput = (fileName: string) => {
        const fileToPreview = outputFiles.find(f => f.name === fileName);
        if (fileToPreview) {
            handleOpenFileViewer(fileToPreview);
        }
    };

    const isActionDisabled = files.length === 0 || !password || status === 'loading';

    const faqs = [
        { q: 'pdf_decrypt_faq1_q', a: 'pdf_decrypt_faq1_a' },
        { q: 'pdf_decrypt_faq2_q', a: 'pdf_decrypt_faq2_a' },
    ];

    const acceptedFiles: Accept = { 'application/pdf': ['.pdf'] };

    return (
        <>
            <PdfViewerModal
                open={viewerFile !== null}
                onClose={handleCloseFileViewer}
                file={viewerFile}
            />
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Stack spacing={4} alignItems="center">
                    <PDFToolWorkspace
                        titleKey="pdf_decrypt"
                        descKey="pdf_decrypt_desc_long"
                        acceptedFiles={acceptedFiles}
                        allowMultiple={false}
                        allowReordering={false}
                        disabled={status === 'loading'}
                        files={files}
                        onDrop={handleDrop}
                        onDelete={handleDelete}
                        onReorder={() => {}}
                        onFileClick={(file) => {/* Do not open encrypted files */}}
                    >
                        {files.length > 0 && status === 'idle' && (
                            <Stack spacing={2} sx={{ width: '100%', maxWidth: 400, mx: 'auto', mt: 3 }}>
                                <TextField
                                    type={showPassword ? 'text' : 'password'}
                                    label={t('pdf_decrypt_password_label', { ns: 'tools' })}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    fullWidth
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Stack>
                        )}
                    </PDFToolWorkspace>

                    <ToolActionArea
                        status={status}
                        onAction={handleDecrypt}
                        onReset={handleReset}
                        onDownloadAll={() => handleDownloadSingle(outputFiles[0]?.name)}
                        onDownloadSingle={handleDownloadSingle}
                        onPreviewOutput={handlePreviewOutput}
                        actionButtonText={t('pdf_decrypt_action_button', { ns: 'tools' })}
                        successTitle={t('pdf_decrypt_success_title', { ns: 'tools' })}
                        successMessage={t('pdf_decrypt_success_desc', { ns: 'tools' })}
                        errorMessage={error}
                        outputFiles={outputFiles}
                        disabled={isActionDisabled}
                    />
                </Stack>

                <Box sx={{ my: 15 }}>
                    <StyledFAQ
                        title={t('faq_title', { ns: 'tools' })}
                        faqs={faqs}
                        t={t}
                    />
                </Box>
            </Container>
        </>
    );
};

export default PdfDecryptor;